import { z } from 'zod';
import { TimestampSchema } from '../common/clock.js';
import { publicIdSchema } from '../common/ids.js';
import { ApplicationPhaseSchema } from '../origination/states.js';
import { AgentRoleSchema } from './states.js';
/**
 * Base de conocimiento (`01-producto/04-capa-agentica.md` §5) y skills (§7).
 * Ciclo de una versión: `DRAFT → IN_REVIEW → PUBLISHED → ARCHIVED`. Solo la versión `PUBLISHED`
 * vigente es visible para los agentes y cada respuesta basada en la KB cita `articleId@version`.
 */
// ── Base de conocimiento ──────────────────────────────────────────────────
export const KnowledgeArticleIdSchema = publicIdSchema('kba');
export const KB_CATEGORY_CODES = [
    'LINEAMIENTOS',
    'PROCESO_ORIGINACION',
    'PRODUCTO',
    'REQUISITOS',
    'MARGENES_SECTOR',
    'FAQ',
    'EDUCACION_FINANCIERA',
    'POLITICA_PAGOS',
    'POLITICA_DISPUTAS',
];
export const KbCategoryCodeSchema = z.enum(KB_CATEGORY_CODES).meta({ id: 'KbCategoryCode' });
export const KbVersionStatusSchema = z
    .enum(['DRAFT', 'IN_REVIEW', 'PUBLISHED', 'ARCHIVED'])
    .meta({ id: 'KbVersionStatus' });
/** Estado agregado del artículo (`kb_articles.status`). */
export const KbArticleStatusSchema = z
    .enum(['DRAFT', 'PUBLISHED', 'ARCHIVED'])
    .meta({ id: 'KbArticleStatus' });
/** `AGENT` = solo uso interno del agente (p. ej. márgenes por sector); nunca se muestra literal al cliente. */
export const KbAudienceSchema = z.enum(['AGENT', 'CUSTOMER', 'BOTH']).meta({ id: 'KbAudience' });
export const KbSlugSchema = z
    .string()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'slug en minúsculas con guiones')
    .max(120);
/** Cita estable: `kba_…@3#seccion`. */
export const KbCitationSchema = z
    .object({
    articleId: KnowledgeArticleIdSchema,
    version: z.int().min(1),
    slug: KbSlugSchema,
    title: z.string(),
    anchor: z.string().nullable(),
    ref: z.string(),
})
    .meta({ id: 'KbCitation' });
export const KbSearchHitSchema = z
    .object({
    citation: KbCitationSchema,
    category: KbCategoryCodeSchema,
    audience: KbAudienceSchema,
    /** Fragmento con los términos resaltados entre `«` y `»`. */
    snippet: z.string(),
    rank: z.number(),
})
    .meta({ id: 'KbSearchHit' });
export const KbSearchRequestSchema = z
    .object({
    query: z.string().trim().min(2).max(300),
    categories: z.array(KbCategoryCodeSchema).max(KB_CATEGORY_CODES.length).optional(),
    limit: z.int().min(1).max(10).default(5),
})
    .meta({ id: 'KbSearchRequest' });
export const KbArticleVersionSchema = z
    .object({
    version: z.int().min(1),
    status: KbVersionStatusSchema,
    contentMd: z.string(),
    changeNote: z.string().nullable(),
    authorId: z.string().nullable(),
    publishedAt: TimestampSchema.nullable(),
    createdAt: TimestampSchema,
})
    .meta({ id: 'KbArticleVersion' });
export const KbArticleSchema = z
    .object({
    id: KnowledgeArticleIdSchema,
    slug: KbSlugSchema,
    title: z.string(),
    category: KbCategoryCodeSchema,
    audience: KbAudienceSchema,
    status: KbArticleStatusSchema,
    /** Versión publicada vigente (null si nunca se publicó). */
    publishedVersion: z.int().nullable(),
    latestVersion: KbArticleVersionSchema,
    isDemoContent: z.boolean(),
    updatedAt: TimestampSchema,
})
    .meta({ id: 'KbArticle' });
export const CreateKbArticleRequestSchema = z
    .object({
    slug: KbSlugSchema,
    title: z.string().trim().min(3).max(200),
    category: KbCategoryCodeSchema,
    audience: KbAudienceSchema.default('BOTH'),
    contentMd: z.string().min(1).max(100_000),
    changeNote: z.string().max(500).optional(),
})
    .meta({ id: 'CreateKbArticleRequest' });
/** Editar crea una nueva versión `DRAFT` (o reemplaza el borrador abierto). */
export const UpdateKbArticleRequestSchema = z
    .object({
    title: z.string().trim().min(3).max(200).optional(),
    category: KbCategoryCodeSchema.optional(),
    audience: KbAudienceSchema.optional(),
    contentMd: z.string().min(1).max(100_000).optional(),
    changeNote: z.string().max(500).optional(),
})
    .meta({ id: 'UpdateKbArticleRequest' });
// ── Skills ────────────────────────────────────────────────────────────────
export const AGENT_SKILL_CODES = [
    'asesoria-operativa-proceso',
    'asesoria-productos',
    'documentacion-requerida',
    'construccion-capacidad-endeudamiento',
    'revision-documental',
    'simulacion-credito',
    'compras-bre-b',
    'disputas',
    'educacion-financiera',
];
export const AgentSkillCodeSchema = z.enum(AGENT_SKILL_CODES).meta({ id: 'AgentSkillCode' });
export const AgentSkillSchema = z
    .object({
    code: AgentSkillCodeSchema,
    title: z.string(),
    roles: z.array(AgentRoleSchema),
    phases: z.array(ApplicationPhaseSchema),
    /** Intenciones que también la cargan (p. ej. `product_question`). */
    intents: z.array(z.string()),
    activeVersion: z.int().nullable(),
    latestVersion: z.int(),
    contentMd: z.string(),
})
    .meta({ id: 'AgentSkill' });
export const CreateSkillVersionRequestSchema = z
    .object({ contentMd: z.string().min(1).max(50_000), changeNote: z.string().max(500).optional() })
    .meta({ id: 'CreateSkillVersionRequest' });
export const ActivateSkillVersionRequestSchema = z
    .object({ version: z.int().min(1) })
    .meta({ id: 'ActivateSkillVersionRequest' });
//# sourceMappingURL=knowledge.js.map